/**
 * Design tokens for the Social Analytics Dashboard
 * Modern analytics aesthetic with gradients and refined spacing
 */

// Metric card gradients - two distinct rows
// Row 1: Video metrics (purple-blue)
export const VIDEO_METRIC_GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
// Row 2: Analysis metrics (teal-green)
export const ANALYSIS_METRIC_GRADIENT = 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'

const BRAND_METRIC_GRADIENT = VIDEO_METRIC_GRADIENT
const BRAND_METRIC_GRADIENT_ALT = ANALYSIS_METRIC_GRADIENT

export const METRIC_GRADIENTS: Record<string, string> = {
  // All metrics use the same brand gradient for visual cohesion
  tiktok_video_reach: BRAND_METRIC_GRADIENT,
  video_views: BRAND_METRIC_GRADIENT,
  total_engagements: BRAND_METRIC_GRADIENT,
  engagement_rate_in_percent: BRAND_METRIC_GRADIENT_ALT,
  likes: BRAND_METRIC_GRADIENT,
  total_likes: BRAND_METRIC_GRADIENT,
  comments: BRAND_METRIC_GRADIENT,
  total_comments: BRAND_METRIC_GRADIENT,
  shares: BRAND_METRIC_GRADIENT,
  total_shares: BRAND_METRIC_GRADIENT,
  saves: BRAND_METRIC_GRADIENT,
  total_saves: BRAND_METRIC_GRADIENT,
  default: BRAND_METRIC_GRADIENT,
}

// Get gradient for a metric key
export const getMetricGradient = (key: string): string => {
  // Try exact match first
  if (METRIC_GRADIENTS[key]) {
    return METRIC_GRADIENTS[key]
  }

  // Try partial match (contains)
  const lowerKey = key.toLowerCase()
  if (lowerKey.includes('reach') || lowerKey.includes('view')) {
    return METRIC_GRADIENTS.tiktok_video_reach
  }
  if (lowerKey.includes('engagement')) {
    return METRIC_GRADIENTS.total_engagements
  }
  if (lowerKey.includes('like')) {
    return METRIC_GRADIENTS.likes
  }
  if (lowerKey.includes('comment')) {
    return METRIC_GRADIENTS.comments
  }
  if (lowerKey.includes('share')) {
    return METRIC_GRADIENTS.shares
  }
  if (lowerKey.includes('save')) {
    return METRIC_GRADIENTS.saves
  }

  return METRIC_GRADIENTS.default
}

// Primary brand gradient for headers and accents
export const BRAND_GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'

// Sky brand gradient (orange → purple → blue)
export const SKY_GRADIENT = 'linear-gradient(90deg, #F5841F 0%, #C02A5A 35%, #722F8E 65%, #0067B9 100%)'

// Brand colors (extracted from gradients for consistent use)
export const BRAND_PURPLE = '#667eea'
export const SKY_BLUE = '#0067B9'

// Chart type colors for icons/accents
export const CHART_COLORS: Record<string, string> = {
  pie: '#667eea',
  bar: '#4facfe',
  column: '#4facfe',
  line: '#11998e',
  stackedBar: '#f5576c',
  stackedColumn: '#f5576c',
  clusteredBar: '#a855f7',
  clusteredColumn: '#a855f7',
  scatter: '#f59e0b',
  table: '#64748b',
}

/**
 * Semantic colors for sentiment and emotion categories
 * These colors are shared between charts and table tags for consistency
 */
export const SENTIMENT_COLORS: Record<string, string> = {
  // Sentiment - pastel tones (10% lighter)
  positive: 'rgba(74, 209, 121, 0.9)',   // Pastel green
  negative: 'rgba(244, 108, 108, 0.9)',  // Pastel red
  neutral: 'rgba(164, 177, 195, 0.9)',   // Pastel slate
  mixed: 'rgba(248, 179, 62, 0.9)',      // Pastel amber
}

export const EMOTION_COLORS: Record<string, string> = {
  // Plutchik's 8 basic emotions - pastel tones (10% lighter)
  joy: 'rgba(252, 216, 72, 0.9)',         // Pastel yellow
  trust: 'rgba(74, 209, 121, 0.9)',       // Pastel green
  fear: 'rgba(185, 117, 249, 0.9)',       // Pastel purple
  surprise: 'rgba(251, 142, 67, 0.9)',    // Pastel orange
  sadness: 'rgba(99, 155, 248, 0.9)',     // Pastel blue
  disgust: 'rgba(156, 216, 67, 0.9)',     // Pastel lime
  anger: 'rgba(244, 108, 108, 0.9)',      // Pastel red
  anticipation: 'rgba(241, 110, 172, 0.9)', // Pastel pink
  // Additional emotions
  none: 'rgba(164, 177, 195, 0.9)',       // Pastel gray
  love: 'rgba(247, 143, 196, 0.9)',       // Pastel pink
  excitement: 'rgba(253, 170, 99, 0.9)',  // Pastel light orange
  frustration: 'rgba(231, 82, 82, 0.9)',  // Pastel dark red
  confusion: 'rgba(162, 124, 248, 0.9)',  // Pastel violet
}

/**
 * Gradient definitions for chart colors (ECharts format)
 * Each gradient goes from pastel at top to lighter at bottom
 */
export const EMOTION_GRADIENTS: Record<string, object> = {
  joy: {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: 'rgba(252, 216, 72, 0.9)' },  // Pastel yellow
      { offset: 1, color: 'rgba(254, 240, 158, 0.9)' }  // Lighter yellow
    ]
  },
  trust: {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: 'rgba(74, 209, 121, 0.9)' },  // Pastel green
      { offset: 1, color: 'rgba(154, 239, 182, 0.9)' }  // Lighter green
    ]
  },
  fear: {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: 'rgba(185, 117, 249, 0.9)' }, // Pastel purple
      { offset: 1, color: 'rgba(220, 180, 254, 0.9)' }  // Lighter purple
    ]
  },
  surprise: {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: 'rgba(251, 142, 67, 0.9)' },  // Pastel orange
      { offset: 1, color: 'rgba(253, 196, 146, 0.9)' }  // Lighter orange
    ]
  },
  sadness: {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: 'rgba(99, 155, 248, 0.9)' },  // Pastel blue
      { offset: 1, color: 'rgba(167, 207, 253, 0.9)' }  // Lighter blue
    ]
  },
  disgust: {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: 'rgba(156, 216, 67, 0.9)' },  // Pastel lime
      { offset: 1, color: 'rgba(200, 242, 130, 0.9)' }  // Lighter lime
    ]
  },
  anger: {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: 'rgba(244, 108, 108, 0.9)' }, // Pastel red
      { offset: 1, color: 'rgba(252, 175, 175, 0.9)' }  // Lighter red
    ]
  },
  anticipation: {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: 'rgba(241, 110, 172, 0.9)' }, // Pastel pink
      { offset: 1, color: 'rgba(249, 178, 212, 0.9)' }  // Lighter pink
    ]
  },
  none: {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: 'rgba(164, 177, 195, 0.9)' }, // Pastel slate
      { offset: 1, color: 'rgba(226, 232, 240, 0.9)' }  // Lighter slate
    ]
  },
  // Sentiment gradients
  positive: {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: 'rgba(74, 209, 121, 0.9)' },  // Pastel green
      { offset: 1, color: 'rgba(154, 239, 182, 0.9)' }  // Lighter green
    ]
  },
  negative: {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: 'rgba(244, 108, 108, 0.9)' }, // Pastel red
      { offset: 1, color: 'rgba(252, 175, 175, 0.9)' }  // Lighter red
    ]
  },
  neutral: {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: 'rgba(164, 177, 195, 0.9)' }, // Pastel slate
      { offset: 1, color: 'rgba(226, 232, 240, 0.9)' }  // Lighter slate
    ]
  },
  mixed: {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: 'rgba(248, 179, 62, 0.9)' },  // Pastel amber
      { offset: 1, color: 'rgba(252, 219, 117, 0.9)' }  // Lighter amber
    ]
  },
}

export const ATTRIBUTION_COLORS: Record<string, string> = {
  // Sky relevance/attribution - muted, refined palette
  yes: '#667eea',        // Brand purple
  no: '#CBD5E1',         // Soft slate grey
  true: '#667eea',
  false: '#CBD5E1',
  relevant: '#667eea',
  'not relevant': '#CBD5E1',
  attributed: '#667eea',
  'not attributed': '#CBD5E1',
}

export const CATEGORY_COLORS: Record<string, string> = {
  // Content type categories - distinct from sentiment/emotion colors
  substantive: '#0891B2',     // Cyan-600 - professional, informative
  brief: '#06B6D4',           // Cyan-500 - quick, concise
  meme: '#D946EF',            // Fuchsia-500 - playful, creative
  emoji: '#FB7185',           // Rose-400 - expressive
  spam: '#64748B',            // Slate-500 - neutral/unwanted
  'emoji only': '#F59E0B',    // Amber-500 - emoji-focused
  'cultural/media reference': '#4F46E5', // Indigo-600 - cultural context
  question: '#8B5CF6',        // Violet-500 - inquiry
  feedback: '#10B981',        // Emerald-500 - constructive
  criticism: '#F43F5E',       // Rose-500 - critical
  praise: '#22D3EE',          // Cyan-400 - positive feedback
  request: '#A78BFA',         // Violet-400 - asking for something
  'off-topic': '#9CA3AF',     // Gray-400 - not relevant
  sarcasm: '#EC4899',         // Pink-500 - indirect meaning
  humor: '#FCD34D',           // Amber-300 - funny content
}

/**
 * Deterministic color generator for unknown codes
 * Uses a hash function to always generate the same color for the same string
 * Returns colors from a curated palette to ensure good visibility
 */
const FALLBACK_PALETTE = [
  '#3B82F6', // Blue-500
  '#8B5CF6', // Violet-500
  '#EC4899', // Pink-500
  '#F97316', // Orange-500
  '#14B8A6', // Teal-500
  '#84CC16', // Lime-500
  '#F43F5E', // Rose-500
  '#6366F1', // Indigo-500
  '#0EA5E9', // Sky-500
  '#A855F7', // Purple-500
  '#22C55E', // Green-500
  '#EAB308', // Yellow-500
  '#F59E0B', // Amber-500
  '#EF4444', // Red-500
  '#06B6D4', // Cyan-500
  '#D946EF', // Fuchsia-500
]

const hashString = (str: string): number => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash)
}

export const getDeterministicColor = (code: string): string => {
  const normalizedCode = code.toLowerCase().trim()
  const hash = hashString(normalizedCode)
  return FALLBACK_PALETTE[hash % FALLBACK_PALETTE.length]
}

/**
 * Combined color map for all social media categories
 * Use this when passing colorMap to RecordTable or charts
 */
export const SOCIAL_COLOR_MAP: Record<string, string> = {
  ...SENTIMENT_COLORS,
  ...EMOTION_COLORS,
  ...ATTRIBUTION_COLORS,
  ...CATEGORY_COLORS,
}

/**
 * The emotion chart's eight colours, for charts of free-form categories (a
 * post tag's values on a pie), ordered so neighbouring slices differ. Never
 * cycled: a ninth value and "Untagged" take the neutral grey, and the pie's
 * own labels name every slice.
 */
export const CATEGORY_SERIES_COLORS: string[] = [
  EMOTION_COLORS.sadness,
  EMOTION_COLORS.surprise,
  EMOTION_COLORS.trust,
  EMOTION_COLORS.anticipation,
  EMOTION_COLORS.joy,
  EMOTION_COLORS.fear,
  EMOTION_COLORS.anger,
  EMOTION_COLORS.disgust,
]

/** Untagged and any slice past the eighth: the emotion chart's grey, half as dark (Kai, 20 Oct 2026). */
export const UNTAGGED_COLOR = 'rgb(210, 216, 225)'

/** A colour map (lower-cased name → colour): each category its own hue in order, Untagged and any past the eighth grey. */
export const categoryColorMap = (labels: string[]): Record<string, string> => {
  const out: Record<string, string> = {}
  let i = 0
  for (const label of labels) {
    const key = label.toLowerCase().trim()
    out[key] = key === 'untagged' ? UNTAGGED_COLOR : (CATEGORY_SERIES_COLORS[i++] ?? UNTAGGED_COLOR)
  }
  return out
}

/**
 * Get color for a code, with fallback to deterministic color generation
 * Always returns a consistent color for the same code string
 */
export const getCodeColor = (code: string): string => {
  const normalizedCode = code.toLowerCase().trim()
  return SOCIAL_COLOR_MAP[normalizedCode] || getDeterministicColor(normalizedCode)
}

/**
 * Get color array in order for chart series
 * Pass the category names in order and get back the color array
 */
export const getChartColors = (categories: string[]): string[] => {
  return categories.map((cat) => {
    const key = cat.toLowerCase().trim()
    return SOCIAL_COLOR_MAP[key] || CHART_COLORS.bar
  })
}

// Surface and background colors
export const SURFACE_COLORS = {
  // Card backgrounds
  cardGradient: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
  cardHover: '0 4px 12px rgba(0,0,0,0.08)',

  // Section backgrounds
  sectionBg: '#fafafa',

  // Filter drawer
  filterHeader: BRAND_GRADIENT,
  filterAccordionHover: 'rgba(0, 0, 0, 0.02)',
  filterAccordionExpanded: 'rgba(0, 0, 0, 0.02)',
}

// Spacing constants
export const SPACING = {
  section: 4,      // 32px - between major sections
  sectionInner: 3, // 24px - within sections
  card: 2,         // 16px - card padding
  cardGap: 2,      // 16px - between cards
}

// Border radius
export const RADIUS = {
  card: 2,       // 8px
  cardLarge: 3,  // 12px
  button: 2,     // 8px
}

// Transitions
export const TRANSITIONS = {
  card: 'transform 0.2s ease, box-shadow 0.2s ease',
  button: 'background-color 0.2s ease',
}
